// Library imports
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import store, { RootState } from "../../../../store/index";

// Local imports
import { IArticlesItem } from "../../interfaces";
import { getSearchArticles, resetSearchedArticles } from "../actions";
import { chunkArray } from "utils";
import MyAccountsArticles from "modules/Content/MyAccounts/Components/myAccountsArticles";
import InfiniteLoader from "modules/Common/Components/InfiniteLoader";
import ListPlaceholder from "modules/Content/WhatsHappeningNow/Components/ListPlaceholder";
import WhnPlaceholder from "modules/Content/WhatsHappeningNow/Components/WhnPlaceholder";
import EmptyTabInfo from "modules/Content/Shared/EmptyTabInfo";
import SelectedBottom from "modules/Common/Components/SelectedBottom";
import { removeSelectedIds } from "modules/Content/DragnDrop/actions";
import { closeOverlay, GLOBAL_ACTION } from "modules/GlobalActions";
import { PREVIEW_ARTICLE, SEARCH_ARTICLES } from "constants/actionTypes";
import { EMAIL_LIST } from "constants/types";
import { getCampaignDataService } from "modules/Services/CampaignsService";

interface SearchArticlesContainer {
  searchTerms: string[];
  isListView: boolean;
  sortBy?: number;
  dateFilter?: { start_date: string; end_date: string };
  favourites?: boolean;
  pageInterested?: boolean;
  inEditionSmartSourced?: boolean;
  isCampaign?: boolean;
  description?: string;
  inContentSmartSourced?: boolean;
  inMyAccounts?: boolean;
  showNews?: boolean;
}

const SearchTab: React.FC<SearchArticlesContainer> = ({
  searchTerms,
  isListView,
  dateFilter,
  sortBy,
  favourites = false,
  inEditionSmartSourced,
  isCampaign,
  description,
  inContentSmartSourced,
  inMyAccounts,
  showNews = true,
}: SearchArticlesContainer) => {
  // Local Variables
  const DEFAULT_PAGE_LIMIT = 50;
  const FIRST_PAGE_LIMIT = 50;

  // Local States
  const [firstLoad, setFirstLoad] = useState(false);
  const [firstMount, setFirstMount] = useState(true);
  const [firstMountCall, setFirstMountCall] = useState(false);
  const [stopFirstMountCall, setStopFirstMountCall] = useState(false);
  const [stopFirstMount, setStopFirstMount] = useState(false);

  // Redux States
  const selectedIds = useSelector((state: RootState) => state.dragDrop.selectedIds);
  const articlesCount: number = useSelector((state: RootState) => state.searchArticles.articlesCount);
  const advancedSearch: boolean = useSelector((state: RootState) => state.uiReducer.advancedSearch);
  const sourcedContent = useSelector((state: RootState) => state.searchArticles.sourcedContent);
  const excludedContent = useSelector((state: RootState) => state.searchArticles.excludedContent);
  const excludedKeywords = useSelector((state: RootState) => state.searchArticles.excludedKeywords);
  const includedKeywords = useSelector((state: RootState) => state.searchArticles.includedKeywords);
  const advancedSearchCheck = useSelector((state: RootState) => state.uiReducer.advancedSearchCheck);
  const campaignId = useSelector((state: RootState) => state.emails.campaignId);
  const articles: IArticlesItem[] = useSelector((state: RootState) => {
    return inMyAccounts ? state.accountsNews.articles : state.searchArticles.data;
  });
  const loading: boolean = useSelector((state: RootState) =>
    inMyAccounts ? state.accountsNews.loading : state.searchArticles.loading
  );
  const showAdvancedSearchSidebar: boolean = useSelector(
    (state: RootState) => state.uiReducer.showAdvancedSearchSidebar
  );
  const emailCreatedInExistingCampaign = useSelector(
    (state: RootState) => state.emails.emailCreatedInExistingCampaign
  );

  // Component Logic
  useEffect(() => {
    if (loading && window.location.pathname.startsWith("/content/search")) {
      store.dispatch({
        type: SEARCH_ARTICLES.LOAD_FAIL,
      });
      store.dispatch({
        type: GLOBAL_ACTION.ADVANCED_SEARCH_CHECK,
        payload: false,
      });
    }
    return () => {
      store.dispatch({
        type: SEARCH_ARTICLES.ADVANCED_SEARCH_RESET_CHECK,
      });
      store.dispatch({
        type: GLOBAL_ACTION.ADVANCED_SEARCH_CHECK,
        payload: false,
      });
      setFirstMountCall(false);
      resetSearchedArticles();
    };
  }, []);

  useEffect(() => {
    if (stopFirstMount) {
      if (inContentSmartSourced && searchTerms.length > 0) {
        getSearchArticles(
          searchTerms,
          0,
          FIRST_PAGE_LIMIT,
          sortBy,
          dateFilter,
          favourites,
          true,
          sourcedContent,
          excludedContent,
          excludedKeywords,
          includedKeywords
        );
      }
    } else {
      setStopFirstMount(true);
    }
  }, [includedKeywords]);

  useEffect(() => {
    if (stopFirstMountCall) {
      if (!inMyAccounts) {
        if (!inContentSmartSourced) {
          if (searchTerms.length > 0 && !advancedSearchCheck) {
            getSearchArticles(searchTerms, 0, FIRST_PAGE_LIMIT, sortBy, dateFilter, favourites);
          } else if (advancedSearchCheck) {
            getSearchArticles(
              [],
              0,
              FIRST_PAGE_LIMIT,
              sortBy,
              dateFilter,
              favourites,
              true,
              sourcedContent,
              excludedContent,
              excludedKeywords,
              includedKeywords
            );
          }
        }
      } else setStopFirstMountCall(true);
    }
  }, [searchTerms, stopFirstMountCall]);

  useEffect(() => {
    if (firstMount) {
      if (includedKeywords.length > 0) {
        getSearchArticles(
          [],
          0,
          FIRST_PAGE_LIMIT,
          sortBy,
          dateFilter,
          favourites,
          true,
          sourcedContent,
          excludedContent,
          excludedKeywords,
          includedKeywords
        );
      }
      setFirstMount(false);
    } else {
      // added '!articles.length' check to avoid calling the API if we have the data in the articles.
      // beacuse the below function call with the offset 0 i.e trying to fetch the page 1 of the API which is already there.
      if ((isCampaign || inEditionSmartSourced) && includedKeywords.length > 0 && !showAdvancedSearchSidebar && !articles.length) {
        getSearchArticles(
          [],
          0,
          FIRST_PAGE_LIMIT,
          sortBy,
          dateFilter,
          favourites,
          true,
          sourcedContent,
          excludedContent,
          excludedKeywords,
          includedKeywords
        );
        setFirstMount(false);
      }
    }
  }, [includedKeywords]);

  useEffect(() => {
    if (inContentSmartSourced) {
      if (firstMountCall) {
        if (!(searchTerms.length === 0 && !advancedSearchCheck)) {
          getSearchArticles(
            searchTerms,
            0,
            FIRST_PAGE_LIMIT,
            sortBy,
            dateFilter,
            favourites,
            advancedSearchCheck,
            sourcedContent,
            excludedContent,
            excludedKeywords,
            includedKeywords
          );
        }
      } else {
        setFirstMountCall(true);
      }
    } else if (inMyAccounts && searchTerms.length > 0) {
      getSearchArticles(searchTerms, 0, FIRST_PAGE_LIMIT, sortBy, dateFilter, favourites);
    } else if ((isCampaign || inEditionSmartSourced) && searchTerms.length > 0 && !advancedSearchCheck) {
      getSearchArticles(searchTerms, 0, FIRST_PAGE_LIMIT, sortBy, dateFilter, favourites);
    }
  }, [searchTerms]);

  useEffect(() => {
    if (firstLoad) {
      if (!window.location.pathname.startsWith("/content/search") && includedKeywords.length > 0) {
        getSearchArticles(
          [],
          0,
          FIRST_PAGE_LIMIT,
          sortBy,
          dateFilter,
          favourites,
          true,
          sourcedContent,
          excludedContent,
          excludedKeywords,
          includedKeywords,
          true
        );
        store.dispatch({
          type: GLOBAL_ACTION.CLOSE_ADVANCED_SEARCH_SIDEBAR,
        });
        store.dispatch({
          type: GLOBAL_ACTION.ADVANCED_SEARCH_CHECK,
          payload: true,
        });
        if (window.location.pathname.startsWith("/campaigns/")) {
          store.dispatch({
            type: PREVIEW_ARTICLE.UPDATE_SEARCH_TERMS,
            payload: includedKeywords,
          });
        }
        closeOverlay();
      } else if (window.location.pathname.startsWith("/content/search")) {
        if (searchTerms.length > 0) {
          getSearchArticles(
            searchTerms,
            0,
            FIRST_PAGE_LIMIT,
            sortBy,
            dateFilter,
            favourites,
            true,
            sourcedContent,
            excludedContent,
            excludedKeywords,
            includedKeywords
          );
          store.dispatch({
            type: GLOBAL_ACTION.CLOSE_ADVANCED_SEARCH_SIDEBAR,
          });
          store.dispatch({
            type: GLOBAL_ACTION.ADVANCED_SEARCH_CHECK,
            payload: true,
          });
          if (window.location.pathname.startsWith("/campaigns/")) {
            store.dispatch({
              type: PREVIEW_ARTICLE.UPDATE_SEARCH_TERMS,
              payload: includedKeywords,
            });
          }
        } else {
          store.dispatch({
            type: GLOBAL_ACTION.CLOSE_ADVANCED_SEARCH_SIDEBAR,
          });
        }
        closeOverlay();
      }
    } else {
      setFirstLoad(true);
    }
  }, [advancedSearch]);

  useEffect(() => {
    if (emailCreatedInExistingCampaign) {
      getCampaignDataService(campaignId).then((res) => {
        const { include_sources, exclude_sources, campaign_keywords, exclude_keywords } = res.data;

        let includeObj: any = [];
        let excludeObj: any = [];

        // extract required keys from include sources
        include_sources.map((obj: any) => {
          includeObj.push((({ data, is_active, type }) => ({ data, is_active, type }))(obj));
        });
        // extract required keys from exclude sources
        exclude_sources.map((obj: any) => {
          excludeObj.push((({ data, is_active, type }) => ({ data, is_active, type }))(obj));
        });
        if (campaign_keywords.length > 0) {
          getSearchArticles(
            [],
            0,
            FIRST_PAGE_LIMIT,
            sortBy,
            dateFilter,
            favourites,
            true,
            includeObj,
            excludeObj,
            exclude_keywords,
            campaign_keywords
          );
        }
        store.dispatch({
          type: EMAIL_LIST.EMAIL_IN_EXISTING_CAMPAIGN,
          payload: false,
        });
      });
    }
  }, [emailCreatedInExistingCampaign]);

  /**
   * sometimes searchTerms are removed yet, api fetches articles
   * in that particular case resetting the searchedArticles data
   * in the following useEffect
   */
  // useEffect(() => {
  // if (articles.length > 0 && searchTerms.length === 0 && includedKeywords.length === 0) {
  // resetSearchedArticles();
  // }
  // }, [articles]);

  // const advancedSearchClickHandler = () => {
  //   store.dispatch({
  //     type: GLOBAL_ACTION.OPEN_ADVANCED_SEARCH_SIDEBAR,
  //   });
  // };

  const getNoContentMessage = () => {
    let message = ''
    if (!inContentSmartSourced) {
      message = 'Oops! No content found. Consider trying different keywords or adjusting the time range to expand your search.'
    }
    else {
      message = 'Oops! No content found. Consider trying different keywords.'
    }
    return message
  }

  return (
    showNews &&
    (searchTerms.length === 0 && articles.length === 0 && !loading ? (
      <>
        {inContentSmartSourced && <ArticleCount>{articlesCount.toLocaleString()} results</ArticleCount>}
        <div className="">
          {/* <ButtonRightIcon onClick={advancedSearchClickHandler} label="Advanced Search" noIcon /> */}
        </div>
        <NoContentText>
          {inMyAccounts
            ? "No recommended articles found against your account selection"
            : advancedSearchCheck
              ? "Oops! No content found. Consider trying different keywords or adjusting the time range to expand your search."
              : description ??
              "Enter keywords in the search bar above to find relevant content that your audiences will love."}
        </NoContentText>
      </>
    ) : (
      <TabOneStyle>
        {/* //? Adding Infinite Loader } */}
        {inContentSmartSourced && <div className="results">{articlesCount.toLocaleString()} results</div>}
        <div className="">
          {/* <ButtonRightIcon onClick={advancedSearchClickHandler} label="Advanced Search" noIcon /> */}
        </div>
        <InfiniteLoader
          dataLength={articles.length}
          next={() => {
            if (advancedSearchCheck || window.location.pathname.startsWith("/content/search")) {
              getSearchArticles(
                window.location.pathname.startsWith("/content/search") ? searchTerms : [],
                articles.length,
                articles.length < 1 ? FIRST_PAGE_LIMIT : DEFAULT_PAGE_LIMIT,
                sortBy,
                dateFilter,
                favourites,
                true,
                sourcedContent,
                excludedContent,
                excludedKeywords,
                includedKeywords
              );
            } else if (searchTerms.length > 0) {
              getSearchArticles(
                searchTerms,
                articles.length,
                articles.length < 1 ? FIRST_PAGE_LIMIT : DEFAULT_PAGE_LIMIT,
                sortBy,
                dateFilter,
                favourites
              );
            }
          }}
          hasMore={true}
          loader={isListView ? <ListPlaceholder loading={loading} /> : <WhnPlaceholder loading={loading} />}
        >
          <div className="">
            {articles.length === 0 &&
              loading === false &&
              searchTerms.length === 0 &&
              includedKeywords.length === 0 ? (
              <EmptyTabInfo
                description={
                  description ??
                  "Enter keywords in the search bar above to find relevant content that your audiences will love."
                }
              />
            ) : null}

            {articles.length === 0 && loading === false && searchTerms.length > 0 ? (
              <EmptyTabInfo
                description={
                  inMyAccounts
                    ? "We can’t find content matching the selected accounts"
                    : getNoContentMessage()
                }
              />
            ) : null}

            {(searchTerms.length > 0 || articles.length > 0) && (
              <>
                {/* <WhnArticlesContainer
                isCampaign={isCampaign}
                inEditionSmartSourced={inEditionSmartSourced}
                articles={articles.slice(0, FIRST_PAGE_LIMIT)}
                isListView={isListView}
              /> */}

                {chunkArray(articles, DEFAULT_PAGE_LIMIT).map((subArticles, index) => (
                  <MyAccountsArticles
                    isCampaign={isCampaign}
                    inEditionSmartSourced={inEditionSmartSourced}
                    key={index}
                    articles={subArticles}
                    isListView={isListView}
                  />
                ))}
              </>
            )}
          </div>
        </InfiniteLoader>
        <SelectedBottom
          hideAction={true}
          selectedCount={selectedIds?.length}
          selectedMessage={` ${selectedIds.length === 1 ? "article" : "articles"} selected`}
          onClearSelection={removeSelectedIds}
        />
      </TabOneStyle>
    ))
  );
};

export default SearchTab;

const TabOneStyle = styled.div`
  display: inline-block;
  width: 100%;
  .results {
    display: flex;
    flex-direction: column;
    justify-content: right;
    text-align: right;
    color: #7e7e7e;
    font-family: "Roboto Slab", serif;
    font-size: 14px;
    font-weight: 500;
    padding-top: 9px;
    margin-left: 20px;
  }
  .first-caption {
    display: flex;
    justify-content: center;
    align-items: center;
    h3 {
      font-size: 44px;
      font-family: roboto_slabregular, serif;
      color: #424b50;
      font-weight: bold;
      line-height: 1;
    }
  }
`;

const NoContentText = styled.span`
  font-family: "Roboto Slab";
  font-size: 20px;
  color: #424b50;
  /* margin-left: 23%; */
  /* margin-top: 75px; */
  /* max-width: 620px; */
  font-weight: bold;
  line-height: 32px;
  margin: 75px auto;
`;

const ArticleCount = styled.div`
  display: flex;
  flex-direction: column;
  text-align: right;
  justify-content: right;
  color: #7e7e7e;
  font-family: "Roboto Slab", serif;
  font-size: 14px;
  font-weight: 500;
  padding-top: 9px;
  margin-left: 20px;
`;
